import { useCallback, useState, useEffect } from 'react';
import { Box, Button, IconButton } from '@material-ui/core';

import { variables } from '@styles';
import { Typography, Accordion, TextField, Icon, TextFieldCallbacks } from '@components';
import { content } from '@content';
import { Libraries } from '@routes';

import { ColorPicker, ColorPickerProps } from '../ColorPicker';

import { DivContainerProps, DivContainerState } from './DivContainer.props';
import { useStyles } from './DivContainer.styles';
// import { EditMode } from '../EditMode';
import { rgbToHex } from '@utils';

/**
 DivContainer component.
 @returns {JSX.Element}
 */

export const DivContainer = ({
  uiType = 'primary',
  initialValues,
  chosenImage,
  isModuleChanged,
  setIsModuleChanged,
  onChange,
  setChosenLibrary,
  onModuleClose,
  onModuleSave,
}: DivContainerProps) => {
  const styles = useStyles();
  const [state, setState] = useState<DivContainerState>(null);
  const [expanded, setExpanded] = useState(true);
  const [showDelete, setShowDelete] = useState(false);

  // useEffect(() => {
  //   let backgroundColor = initialValues?.backgroundColor ?? '';

  //   if (backgroundColor.includes('rgb')) {
  //     const numbers = backgroundColor
  //       .replace('rgb(', '')
  //       .replace(')', '')
  //       .split(', ')
  //       .map((number) => Number(number));

  //     backgroundColor = rgbToHex(numbers[0], numbers[1], numbers[2]);
  //   }
  //   if (initialValues) setState((prevState) => ({ ...prevState, ...initialValues, backgroundColor }));
  // }, [JSON.stringify(initialValues)]);

  useEffect(() => {
    if (!initialValues) return;

    let backgroundColor = initialValues.backgroundColor ?? '';

    // Match rgb or rgba with or without spaces
    const rgbMatch = backgroundColor.match(/^rgb(a)?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (rgbMatch) {
      backgroundColor = rgbToHex(Number(rgbMatch[2]), Number(rgbMatch[3]), Number(rgbMatch[4]));
    }

    setState((prevState) => ({
      ...prevState,
      ...initialValues,
      backgroundColor,
    }));
  }, [initialValues]);

  useEffect(() => onChange(state), [state, onChange]);

  const isValidHtmlColor = (color: string) => {
    // Accepts hex (#fff, #ffffff), rgb(), rgba(), hsl(), hsla(), and named colors
    if (typeof color !== 'string') return false;
    // Hex
    if (/^#([0-9a-fA-F]{3}){1,2}$/.test(color)) return true;
    // rgb/rgba
    if (/^rgb(a)?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(\s*,\s*(0|1|0?\.\d+))?\s*\)$/.test(color)) return true;
    // hsl/hsla
    if (/^hsl(a)?\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%(\s*,\s*(0|1|0?\.\d+))?\s*\)$/.test(color)) return true;
    // Named colors (basic check)
    const s = document.createElement('span').style;
    s.color = '';
    s.color = color;
    return !!s.color;
  };

  const handleBackgroundColorChange = useCallback<ColorPickerProps['onColorChange']>(
    (backgroundColor) => {
      if (!isValidHtmlColor(backgroundColor)) {
        backgroundColor = 'unset';
      }
      setState((prev) => {
        return { ...prev, backgroundColor };
      });
      setIsModuleChanged(true);
    },
    [setState, setIsModuleChanged],
  );

  const handleSelectImage = useCallback(() => {
    setChosenLibrary(Libraries.IMAGES);
  }, [setChosenLibrary]);

  const handleImageChange = useCallback<TextFieldCallbacks['onChange']>(
    (imageSrc) => {
      setState((prev) => ({ ...prev, imageSrc }));
      setIsModuleChanged(true);
    },
    [setState, setIsModuleChanged],
  );

  const handleDeleteImage = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      imageSrc: 'none',
    }));
    setIsModuleChanged(true);
  }, [setState, setIsModuleChanged]);

  const toggleAccordion = useCallback(() => setExpanded((prevState) => !prevState), [setExpanded]);

  useEffect(() => {
    setState((prevState) => ({ ...prevState, imageSrc: chosenImage.imageSrc || 'none' }));
  }, [chosenImage]);

  useEffect(() => {
    setShowDelete(!!state?.imageSrc && state?.imageSrc !== 'none');
  }, [state?.imageSrc, setShowDelete]);

  return (
    <Box className={styles.divContainer} data-ui-type={uiType}>
      {/* <Box className={styles.titleWrapper}>
        <Typography.Title className={styles.title}>{content.divContainer}</Typography.Title>
      </Box> */}
      <Box className={styles.panel}>
        <Box className={styles.panelContent}>
          <Accordion expanded={expanded} onChange={toggleAccordion} title={content.styling} uiType="quaternary">
            <Box className={styles.topSection}>
              <Typography.SuperSmallCaption className={styles.caption}>
                {content.backgroundImage}
              </Typography.SuperSmallCaption>
              <Box className={styles.imageSrcWrapper}>
                <TextField
                  className={styles.imageSrc}
                  size="small"
                  direction="vertical"
                  placeholder={content.none}
                  value={state?.imageSrc === 'none' ? '' : state?.imageSrc}
                  onChange={handleImageChange}
                  disabled={true}
                />
                <Button className={styles.selectBtn} variant="outlined" onClick={handleSelectImage}>
                  {content[state?.imageSrc && state.imageSrc !== 'none' ? 'change' : 'select']}
                </Button>
                {showDelete && (
                  <IconButton className={styles.deleteButton} onClick={handleDeleteImage}>
                    <Icon.TrashOutline />
                  </IconButton>
                )}
              </Box>
            </Box>
            <Box>
              <Typography.SuperSmallCaption className={styles.caption}>
                {content.backgroundColor}
              </Typography.SuperSmallCaption>
              <ColorPicker
                position="left"
                positionRelative={true}
                color={state?.backgroundColor ?? variables.color.primary.white}
                onColorChange={handleBackgroundColorChange}
              />
            </Box>
          </Accordion>
          <Box className={styles.buttonContainer}>
            <Button className={styles.close} color="primary" variant="outlined" size="medium" onClick={onModuleClose}>
              {content.close}
            </Button>
            <Button
              className={styles.save}
              variant="contained"
              color="secondary"
              size="medium"
              disabled={!isModuleChanged}
              onClick={isModuleChanged ? onModuleSave : undefined}
            >
              {content.save}
            </Button>
          </Box>
        </Box>
      </Box>
      {/* <Box className={styles.editModeContainer}>
        <EditMode />
      </Box> */}
    </Box>
  );
};
